<template>
  <div
    ref="table"
    :layout="layout"
    :apiUrl="apiUrl"
    :paginationSize="paginationSize"
    :autoColumns="autoColumns"
    :columns="columns"
    :paginationSizeSelector="paginationSizeSelector"
    :modelKey="modelKey"
  ></div>
</template>

<script>
import { TabulatorFull as Tabulator } from "tabulator-tables"; //import Tabulator library
import Api from "@/objects/api";
import store from "@/store";
import Model from "@/objects/model";
import Service from "@/objects/service";

export default {
  name: "BreadTabulator",
  props: {
    apiUrl: {
      type: String,
      default: "",
    },
    layout: {
      type: String,
      default: "",
    },
    paginationSize: {
      type: Number,
      default: 10,
    },
    autoColumns: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    paginationSizeSelector: {
      type: Array,
      default: () => [10, 20, 30, 40, 50],
    },
    modelKey: {
      type: String,
      default: "",
    },
    columnSelectionDisabledCount: {
      type: Number,
      default: 0,
    },
    responsiveLayout: {
      type: Boolean,
      default: true,
    },
    // When true, suppress Tabulator's one-time initial (unfiltered) auto-load.
    // The parent is then responsible for applying a view, which fires the
    // single real request. Defaults to false so other consumers are unaffected.
    deferInitialLoad: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tabulator: null, //variable to hold your table
    api: new Api(),
    model: new Model(),
    service: new Service(),
    tableResponseStatus: "",
    condition: "",
    tableTotalRows: 0,
    tableCurrentPage: 1,
  }),
  created() {
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Non-reactive state used to coalesce a synchronous burst of remote
    // reloads (setFilter/setSort/setPageSize/setPage) into one network request.
    this._coalesceTimer = null;
    this._coalescePromise = null;
    this._pendingRequest = null;

    // When deferInitialLoad is set, swallow the very first (auto) load so the
    // only network request is the one that applies the saved view.
    this._skipInitialLoad = this.deferInitialLoad;
  },
  beforeDestroy() {
    if (this._coalesceTimer) {
      clearTimeout(this._coalesceTimer);
      this._coalesceTimer = null;
    }
  },
  mounted() {
    const vm = this;
    const tabulatorOptions = {
      pagination: true,
      layout: this.layout,
      responsiveLayout: this.responsiveLayout,
      frozenRows: true,
      layoutColumnsOnNewData: true,
      validationMode: "highlight",
      resizableColumnFit: true,
      paginationMode: "remote", //enable remote pagination
      ajaxURL: this.apiUrl, //set url for ajax request
      ajaxConfig: {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + store.state.auth.data.token,
        },
      },
      // Coalesce the burst of remote reloads triggered when a saved view is
      // applied (filter + sort + page size + page) into a single request.
      // `this` here is Tabulator's internal Ajax module.
      ajaxRequestFunc: function (url, config, params) {
        return vm.coalesceRequest(this, url, config, params);
      },
      sortMode: "remote", //enable remote sort
      filterMode: "remote", //enable remote filter
      paginationSize: this.paginationSize, //optional parameter to request a certain number of rows per page
      paginationButtonCount: 3,
      autoColumns: this.autoColumns,
      columns: this.columns,
      paginationSizeSelector: this.paginationSizeSelector,
      movableColumns: true,
      placeholder: "No Record Found",
      footerElement: "<span id='crm_footer_count'></span>",
      columnHeaderSortMulti: true,
      ajaxResponse: function (url, params, response) {
        // Must configure with server side response
        let last_page = response.info.totalPages;
        let totalItems = response.info.totalItems;
        const page = params.page || 1;
        const pageSize = params.size || 10;
        const startIndex = (page - 1) * pageSize;
        // Modify the response data to include row numbers
        const rows = response.data || [];
        rows.forEach((row, index) => {
          row.rownum = startIndex + index + 1;
        });

        store.commit("assignDeveloperData", {
          paginationPageNo: page,
        });

        return {
          data: rows,
          last_page: last_page,
          last_row: totalItems,
        };
      },
      paginationCounter: function (
        _pageSize,
        _currentRow,
        currentPage,
        totalRows,
        totalPages
      ) {
        return `showing ${currentPage} - ${totalPages} pages, ${totalRows} results found`;
      },
    };

    // Add grouping if is trashed tab.
    if (this.$route.query.viewId == "trashed") {
      tabulatorOptions.groupBy = (data) => {
        let date = new Date(data.timestampDeleted);
        let year = date.getFullYear();
        let month = ("0" + (date.getMonth() + 1)).slice(-2);
        let day = ("0" + date.getDate()).slice(-2);
        return `${year}-${month}-${day}`; // Group by "YYYY-MM"
      };

      tabulatorOptions.groupHeader = (value, count) => {
        return (
          value +
          "<span style='color:#d00; margin-left:10px;'>(" +
          count +
          " application)</span>"
        );
      };
    }

    this.tabulator = new Tabulator(this.$refs.table, tabulatorOptions);
    this.tabulator.on("cellEdited", (cell) => {
      this.updateCellChanges(cell);
    });
    this.tabulator.on("dataProcessed", (data) => {
      return this.$emit("response", data);
    });
    this.tabulator.on("pageSizeChanged", (pagesize) => {
      this.$emit("getCurrentPageSize", pagesize); // emit to parent
    });
    this.tabulator.on("dataSorting", (sorters) => {
      this.$emit("getCurrentSort", sorters);
    });
    this.tabulator.on("columnResized", (column) => {
      this.$emit("getCurrentColumnWidth", column); // emit to parent
    });
    this.tabulator.on("pageLoaded", (pageno) => {
      // Set pagination data.
      this.$store.commit("assignDeveloperData", {
        paginationPageNo: pageno,
      });

      this.$emit("getCurrentPageNumber", pageno);
    });
    // get the selected rows and emit to parent
    this.tabulator.on("rowSelectionChanged", (data) => {
      this.$emit("getSelectedRows", data); // emit to parent
    });
    this.tabulator.on("dataLoadError", (error) => {
      this.$emit("getDataLoadErrorCode", error);
    });
  },
  methods: {
    // Collapse all remote reloads requested within the same task (plus the
    // microtasks that follow, e.g. the filter watcher) into one network call.
    // Tabulator's DataLoader already discards every response except the last
    // via `requestOrder`, so previously the intermediate reloads were wasted
    // network round-trips. Here we make only the final state hit the network.
    coalesceRequest(ajaxModule, url, config, params) {
      // Swallow the one-time initial auto-load when deferred. The returned
      // promise never resolves, so the table keeps its loading state (no empty
      // flash, no network call) until the parent applies a view and fires the
      // real request.
      if (this._skipInitialLoad) {
        this._skipInitialLoad = false;
        return new Promise(() => {});
      }

      // Last writer wins: the most recent call carries the fully-applied
      // filter + sort + page-size + page state.
      this._pendingRequest = { ajaxModule, url, config, params };

      if (!this._coalescePromise) {
        this._coalescePromise = new Promise((resolve, reject) => {
          this._coalesceTimer = setTimeout(() => {
            const req = this._pendingRequest;
            this._coalescePromise = null;
            this._coalesceTimer = null;
            this._pendingRequest = null;
            this.sendCoalescedRequest(req).then(resolve, reject);
          }, 0);
        });
      }

      return this._coalescePromise;
    },
    // Faithful re-implementation of Tabulator's default ajax loader, reusing
    // the table's own URL generator so the serialised query string
    // (filter[i][field], sort[i][field], page, size) is identical to built-in.
    sendCoalescedRequest({ ajaxModule, url, config, params }) {
      const requestUrl = ajaxModule.urlGenerator.call(
        ajaxModule.table,
        url,
        config,
        params
      );

      const requestConfig = Object.assign({}, config);
      requestConfig.headers = Object.assign(
        { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        config.headers || {}
      );
      if (typeof requestConfig.mode === "undefined") requestConfig.mode = "cors";
      if (typeof requestConfig.credentials === "undefined")
        requestConfig.credentials = "same-origin";

      // Build the body for non-GET requests, mirroring the default loader.
      if (requestConfig.method && requestConfig.method.toUpperCase() !== "GET") {
        const ctOption = ajaxModule.table.options.ajaxContentType;
        const contentType =
          typeof ctOption === "object"
            ? ctOption
            : ajaxModule.contentTypeFormatters[ctOption];
        if (contentType) {
          requestConfig.headers = Object.assign(
            {},
            contentType.headers,
            requestConfig.headers
          );
          requestConfig.body = contentType.body.call(
            ajaxModule,
            requestUrl,
            requestConfig,
            params
          );
        }
      }

      return fetch(requestUrl, requestConfig).then((response) => {
        if (response.ok) return response.json();
        return Promise.reject(response);
      });
    },
    // Escape hatch for deferInitialLoad: if the parent never applies a view
    // (e.g. the view list request failed), trigger a plain load so the table
    // doesn't remain stuck in its loading state.
    releaseDeferredLoad() {
      this._skipInitialLoad = false;
      if (this.tabulator) this.tabulator.setData(this.apiUrl);
    },
    getFilterCondition(filterData) {
      this.tabulator.setFilter(filterData);
    },
    setAdvancedFilterConditions(advanceFilterParams) {
      this.tabulator.setData(this.apiUrl, {
        advanceFilterParam: JSON.stringify(advanceFilterParams),
      });
    },
    applyColumnSelection(selectedColumns) {
      let defaultColumnsFalse = this.columns.filter((column) => {
        if (column.showByDefault == false) {
          return column;
        }
      });

      if (
        selectedColumns.length ==
        this.columns.length -
          (this.columnSelectionDisabledCount + defaultColumnsFalse.length)
      ) {
        // hide if all default false columns
        this.tabulator.setColumns(this.columns);
        for (let column in defaultColumnsFalse) {
          this.tabulator.hideColumn(defaultColumnsFalse[column].field);
        }
        return;
      }

      for (let column in this.columns) {
        if (selectedColumns.includes(this.columns[column].field)) {
          this.tabulator.showColumn(this.columns[column].field);
        } else {
          this.tabulator.hideColumn(this.columns[column].field);
        }
      }
    },
    updateCellChanges(cell) {
      this.$emit("updateEditedCellData", cell); // emit to parent
    },
    callApiUrl(setApiUrl) {
      this.tabulator.setData(setApiUrl);
    },
    callSavedPageSize(pageSize) {
      this.tabulator.setPageSize(pageSize);
    },
    callSavedColumnSort(columnSort) {
      this.tabulator.setSort(columnSort);
    },
    callSavedHeaderWidth(columnWidth) {
      if (columnWidth.length == 0) {
        this.columns.forEach((column) => {
          column.width = "";
        });
      }

      for (let column in this.columns) {
        for (let key in columnWidth) {
          if (this.columns[column].field === columnWidth[key].field) {
            this.columns[column].width = columnWidth[key].width;
          }
        }
      }
    },
    setCurrentPagNumber(pageNo) {
      this.tabulator.setPage(pageNo);
    },
    setCurrentPagSize(pageSize) {
      this.tabulator.setPageSize(pageSize);
    },
    //BOC:[update table data]
    updateTableRowData(rowId, rowname, rowvalue) {
      this.tabulator.updateData([{ id: rowId, [rowname]: rowvalue }]);
    },
    // clear table data
    clearTableData() {
      this.tabulator.setData([]);
      this.tabulator.clearData();
    },
    // Function to update the URL with page number and page size
    updateUrlParams(page, pageSize) {
      const url = new URL(window.location);
      url.searchParams.set("page", page);
      url.searchParams.set("pageSize", pageSize);
      window.history.pushState({}, "", url);
    },
  },
};
</script>
